import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { motion } from "framer-motion";

import { cva } from "class-variance-authority";
import { Table, TableBody, TableCell, TableRow } from "./table";
import { cn } from "../../lib/utils";
import { TablePagination } from "./table-pagination";
import { CustomTableHeader } from "./table-header";
import { EmptyState } from "./empty-state";
import { UIContext } from "../../providers/UIProvider";

export const tableVariants = cva("w-full", {
  variants: {
    variant: {
      default: "",
      striped: "",
      bordered: "[&_td]:border [&_th]:border",
      cards: "grid gap-4 [&_table]:hidden sm:[&_table]:block",
      transaction:
        "border-collapse [&_tr]:border-b [&_tr]:BookEZ_table_border_color",
    },
    cellPadding: {
      none: "[&_td]:p-0 [&_th]:p-0",
      sm: "[&_td]:p-1 [&_th]:p-1",
      default: "[&_td]:p-2 [&_th]:p-2",
      md: "[&_td]:p-3 [&_th]:p-3",
      lg: "[&_td]:p-4 [&_th]:p-4",
    },
  },
  defaultVariants: {
    variant: "striped",
    cellPadding: "default",
  },
});

export type TableVariant =
  | "default"
  | "striped"
  | "bordered"
  | "cards"
  | "transaction";
export type TableSize = "default" | "sm" | "lg" | "xl";
export type TableCellPadding = "none" | "sm" | "default" | "md" | "lg";

interface DataTableProps<TData, TValue> {
  className?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  variant?: TableVariant;
  size?: TableSize;
  cellPadding?: TableCellPadding;
  currentPage?: number;
  showPagination?: boolean;
  pageSize?: number;
  totalPages?: number;
  totalRecords?: number;
  onPageChange?: (size: number) => void;
  onPageSizeChange?: (page: number) => void;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  state?: {
    rowSelection?: RowSelectionState;
    sorting?: SortingState;
    columnFilters?: ColumnFiltersState;
    columnVisibility?: VisibilityState;
  };
}

export function DataTable<TData, TValue>({
  className,
  columns,
  data,
  variant = "striped",
  cellPadding = "default",
  showPagination = false,
  pageSize = 10,
  currentPage = 1,
  totalPages = 1,
  totalRecords = 0,
  onPageChange,
  onPageSizeChange,
  rowSelection,
  onRowSelectionChange,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const { theme } = React.useContext(UIContext);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  const table = useReactTable({
    data,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange,
    initialState: {
      pagination: {
        pageSize,
      },
    },
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <motion.div
      initial={{ opacity: 0 }} // Start with 0 opacity
      animate={{ opacity: 1 }} // Animate to 1 opacity
      transition={{ duration: 1 }} // 1 second fade-in duration
    >
      <div className="w-full overflow-x-auto">
        <div
          className={`${className} min-w-[800px] overflow-hidden transition-all duration-300 
          relative w-full border BookEZ_table_border_color
          ${
            data?.length == 0 || showPagination == false
              ? "rounded-[12px]"
              : "rounded-b-none rounded-t-[12px]"
          }`}
        >
          <Table
            className={cn(
              `overflow-x-auto`,
              tableVariants({
                variant,
                cellPadding,
              })
            )}
          >
            <CustomTableHeader
              headerGroups={table.getHeaderGroups()}
              variant={variant}
            />
            <TableBody>
              {table?.getRowModel()?.rows?.length ? (
                table?.getRowModel()?.rows?.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      // data-state={row.getIsSelected() ? "selected" : undefined}
                      className={cn(
                        "transition-colors duration-200 BookEZ_font_light_bold BookEZ_table_border_color border-b",
                        variant !== "transaction" &&
                          "hover:BookEZ_table_bg_hover_color",
                        variant === "striped" && row.index % 2 === 1
                          ? "BookEZ_table_bg_stripe_color"
                          : "BookEZ_table_bg_odd_stripe_color"
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="px-4 py-3"
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <EmptyState theme={theme} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {showPagination && data?.length > 0 && (
          <TablePagination
            data={data}
            pageSize={pageSize}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            totalRecords={totalRecords}
          />
        )}
      </div>
    </motion.div>
  );
}
